import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, euro, formatApiError, shortDate } from "../lib/api";
import { toast } from "sonner";
import { LockKey, DownloadSimple, LockKeyOpen, Warning, CaretRight, Clock } from "@phosphor-icons/react";

function toCsv(rows) {
    if (!rows.length) return "";
    const headers = ["year_month", "pnl", "entrate", "accrediti", "uscite", "clients_outstanding", "closed_by", "closed_at"];
    const escape = (v) => {
        const s = v === null || v === undefined ? "" : String(v);
        return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [headers.join(";")];
    rows.forEach((r) => lines.push(headers.map((h) => escape(r[h])).join(";")));
    return lines.join("\n");
}

function download(filename, content, mime = "text/csv;charset=utf-8") {
    const blob = new Blob(["\uFEFF" + content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

function lastMonthYm() {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().slice(0, 7);
}

function previousMonthYm() {
    return lastMonthYm();
}

export default function Closures() {
    const navigate = useNavigate();
    const { agencyId, user } = useAuth();
    const [closures, setClosures] = useState([]);
    const [pending, setPending] = useState([]);
    const [confirm, setConfirm] = useState(null); // {ym}
    const [saving, setSaving] = useState(false);
    const [reopenTarget, setReopenTarget] = useState(null);
    const [reopening, setReopening] = useState(false);
    const [targetYm, setTargetYm] = useState(lastMonthYm());
    const [bulkRunning, setBulkRunning] = useState(false);
    const canClose = ["owner", "agency_manager"].includes(user?.role);

    const load = () => {
        if (!agencyId) return;
        api.get(`/closures`, { params: { agency_id: agencyId } }).then((r) =>
            setClosures(r.data),
        );
        api.get(`/closures/pending`, { params: { agency_id: agencyId } }).then((r) =>
            setPending(r.data),
        );
    };
    useEffect(() => {
        load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [agencyId]);

    const close = async () => {
        if (!confirm) return;
        setSaving(true);
        try {
            const r = await api.post("/closures", {
                agency_id: agencyId,
                year_month: confirm.ym,
            });
            toast.success(`Mese ${r.data.year_month} chiuso. P&L ${euro(r.data.pnl)}`);
            setConfirm(null);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const reopen = async () => {
        if (!reopenTarget) return;
        setReopening(true);
        try {
            const r = await api.delete(`/closures/${reopenTarget.id}`);
            toast.success(
                `Mese ${reopenTarget.year_month} riaperto.${r.data.reversed > 0 ? ` Accantonamento ${euro(r.data.reversed)} restituito.` : ""}`,
            );
            setReopenTarget(null);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setReopening(false);
        }
    };

    const exportCsv = () => {
        if (!closures.length) {
            toast.info("Nessuna chiusura da esportare");
            return;
        }
        const csv = toCsv(closures);
        download(`pvrpilot-chiusure-${new Date().toISOString().slice(0,10)}.csv`, csv);
        toast.success("CSV scaricato");
    };

    const closeSingle = async (ym) => {
        const r = await api.post("/closures", {
            agency_id: agencyId,
            year_month: ym,
        });
        return r.data;
    };

    const closeAllPending = async () => {
        setBulkRunning(true);
        try {
            // Chiusura sequenziale in ordine cronologico (i pending sono già ordinati)
            for (const p of pending) {
                const c = await closeSingle(p.year_month);
                toast.success(`Chiuso ${c.year_month} · P&L ${euro(c.pnl)}`);
            }
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
            load();
        } finally {
            setBulkRunning(false);
        }
    };

    const closeFromPending = async (ym) => {
        setSaving(true);
        try {
            const c = await closeSingle(ym);
            toast.success(`Mese ${c.year_month} chiuso · P&L ${euro(c.pnl)}`);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const currentMonth = new Date().toISOString().slice(0, 7);
    const previousMonth = previousMonthYm();
    const closedSet = new Set(closures.map((c) => c.year_month));
    const targetIsClosed = closedSet.has(targetYm);
    const targetIsCurrentOrFuture = targetYm >= currentMonth;

    return (
        <div data-testid="closures-page">
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
                <div>
                    <div className="overline mb-2">Chiusure</div>
                    <h1 className="text-4xl md:text-5xl font-black tracking-tighter">
                        Chiusura Mese
                    </h1>
                </div>
                {canClose && (
                    <div className="flex items-center gap-2 flex-wrap">
                        <button
                            onClick={exportCsv}
                            className="btn-secondary flex items-center gap-2"
                            data-testid="export-csv-btn"
                            disabled={closures.length === 0}
                        >
                            <DownloadSimple size={14} weight="bold" /> CSV
                        </button>
                    </div>
                )}
            </div>

            {canClose && pending.length > 0 && (
                <PendingSection
                    pending={pending}
                    bulkRunning={bulkRunning}
                    saving={saving}
                    onCloseOne={closeFromPending}
                    onCloseAll={closeAllPending}
                />
            )}

            {canClose && (
                <div className="mb-8 p-6 bg-white border border-zinc-200">
                    <div className="overline mb-3">Chiudi un mese</div>
                    <div className="grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-3 items-end">
                        <label className="block">
                            <div className="overline mb-2">Mese da chiudere</div>
                            <input
                                type="month"
                                className="inp"
                                value={targetYm}
                                max={previousMonth}
                                onChange={(e) => setTargetYm(e.target.value)}
                                data-testid="target-ym"
                            />
                        </label>
                        <div className="text-xs text-zinc-600 leading-relaxed">
                            {targetIsClosed && (
                                <span className="text-green-700 font-bold">
                                    ✓ {targetYm} è già chiuso. Puoi riaprirlo dalla tabella sotto.
                                </span>
                            )}
                            {!targetIsClosed && targetIsCurrentOrFuture && (
                                <span className="text-red-600 font-bold">
                                    Non puoi chiudere il mese in corso o un mese futuro.
                                    Aspetta il 1° del mese successivo.
                                </span>
                            )}
                            {!targetIsClosed && !targetIsCurrentOrFuture && (
                                <span>
                                    Pronto a chiudere {targetYm}. Verrà creato lo snapshot
                                    e il P&L del mese accantonato negli Utili.
                                </span>
                            )}
                        </div>
                        <button
                            onClick={() => setConfirm({ ym: targetYm })}
                            disabled={targetIsClosed || targetIsCurrentOrFuture}
                            className="btn-primary flex items-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed"
                            data-testid="close-month-btn"
                        >
                            <LockKey size={14} weight="bold" /> Chiudi {targetYm}
                        </button>
                    </div>
                </div>
            )}

            <div className="mb-8 p-6 bg-white border border-zinc-200">
                <div className="overline mb-3">Come funziona</div>
                <p className="text-sm text-zinc-700 leading-relaxed max-w-3xl">
                    La chiusura mese crea uno <strong>snapshot</strong> dei saldi (wallet, fidi,
                    debiti clienti) e registra il <strong>P&L del mese</strong>. Se il P&L è
                    positivo, l'importo viene automaticamente accantonato nel wallet{" "}
                    <strong>Utili Accantonati</strong>. Puoi <strong>riaprire</strong> una
                    chiusura in qualsiasi momento: l'accantonamento verrà stornato
                    automaticamente e il mese tornerà aperto.
                </p>
            </div>

            <div className="bg-white border border-zinc-200 overflow-x-auto">
                <table className="ft-table" data-testid="closures-table">
                    <thead>
                        <tr>
                            <th>Mese</th>
                            <th className="text-right">Entrate</th>
                            <th className="text-right">Accrediti</th>
                            <th className="text-right">Uscite</th>
                            <th className="text-right">P&L</th>
                            <th className="text-right">Sospesi alla chiusura</th>
                            <th>Chiuso da</th>
                            <th>Data</th>
                            <th className="text-right">Azioni</th>
                        </tr>
                    </thead>
                    <tbody>
                        {closures.map((c) => (
                            <tr
                                key={c.id}
                                data-testid={`closure-row-${c.year_month}`}
                                onClick={() => navigate(`/closures/${c.id}`)}
                                className="cursor-pointer hover:bg-zinc-50 transition-colors"
                            >
                                <td className="font-bold">
                                    <div className="flex items-center gap-2">
                                        {c.year_month}
                                        <CaretRight size={12} weight="bold" className="text-zinc-400" />
                                    </div>
                                </td>
                                <td className="text-right tabular-nums">{euro(c.entrate)}</td>
                                <td className="text-right tabular-nums">{euro(c.accrediti)}</td>
                                <td className="text-right tabular-nums">{euro(c.uscite)}</td>
                                <td
                                    className="text-right tabular-nums font-bold"
                                    style={{ color: c.pnl >= 0 ? "#00C853" : "#FF2A00" }}
                                >
                                    {euro(c.pnl)}
                                </td>
                                <td className="text-right tabular-nums">
                                    {euro(c.clients_outstanding)}
                                </td>
                                <td className="text-zinc-600 text-xs">{c.closed_by}</td>
                                <td className="text-zinc-600 text-xs mono">
                                    {shortDate(c.closed_at)}
                                </td>
                                <td className="text-right" onClick={(e) => e.stopPropagation()}>
                                    <div className="flex items-center justify-end gap-1">
                                        {canClose && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setReopenTarget(c);
                                                }}
                                                className="inline-flex items-center justify-center w-9 h-9 text-zinc-500 hover:text-amber-700 hover:bg-zinc-100"
                                                data-testid={`reopen-closure-${c.year_month}`}
                                                aria-label="Riapri mese"
                                                title="Riapri mese (storna accantonamento)"
                                            >
                                                <LockKeyOpen size={14} weight="bold" />
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {closures.length === 0 && (
                            <tr>
                                <td colSpan={9} className="text-center text-zinc-500 py-10">
                                    Nessuna chiusura registrata
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {confirm && (
                <div
                    className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                    onClick={() => setConfirm(null)}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="w-full max-w-md bg-white border border-zinc-200 p-8"
                    >
                        <div className="overline mb-3">Conferma chiusura</div>
                        <h2 className="text-3xl font-black tracking-tighter mb-6">
                            Chiudere {confirm.ym}?
                        </h2>
                        <p className="text-sm text-zinc-700 mb-8">
                            Verrà creato uno snapshot e il P&L del mese (positivo o negativo) verrà accantonato nel wallet
                            Utili Accantonati. Puoi sempre <strong>riaprire</strong> la chiusura se necessario.
                        </p>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setConfirm(null)}
                                className="btn-secondary flex-1"
                            >
                                Annulla
                            </button>
                            <button
                                onClick={close}
                                disabled={saving}
                                className="btn-primary flex-1"
                                data-testid="close-month-confirm"
                            >
                                {saving ? "Chiusura…" : "Conferma"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {reopenTarget && (
                <div
                    className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                    onClick={() => setReopenTarget(null)}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="w-full max-w-md bg-white border border-zinc-200 p-8"
                        data-testid="reopen-modal"
                    >
                        <div className="overline mb-3">Riapri mese</div>
                        <h2 className="text-3xl font-black tracking-tighter mb-6">
                            Riaprire {reopenTarget.year_month}?
                        </h2>
                        <p className="text-sm text-zinc-700 mb-4">
                            La chiusura verrà eliminata e il mese tornerà aperto.
                        </p>
                        {(reopenTarget.accantonato || reopenTarget.pnl > 0) && (
                            <div className="mb-6 p-3 bg-zinc-50 border border-zinc-200 text-xs text-zinc-700">
                                L'accantonamento di <strong>{euro(reopenTarget.accantonato || reopenTarget.pnl)}</strong> verrà
                                stornato dal wallet Utili Accantonati.
                            </div>
                        )}
                        <div className="flex gap-2">
                            <button
                                onClick={() => setReopenTarget(null)}
                                className="btn-secondary flex-1"
                            >
                                Annulla
                            </button>
                            <button
                                onClick={reopen}
                                disabled={reopening}
                                className="btn-destructive flex-1"
                                data-testid="reopen-confirm"
                            >
                                {reopening ? "Riapertura…" : "Riapri"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function PendingSection({ pending, bulkRunning, saving, onCloseOne, onCloseAll }) {
    const withActivity = pending.filter((p) => p.n_transactions > 0);
    const emptyCount = pending.length - withActivity.length;
    return (
        <div
            className="mb-8 p-6 bg-amber-50 border-2 border-amber-400"
            data-testid="pending-closures-section"
        >
            <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                    <Clock size={22} weight="fill" className="mt-0.5 text-amber-700 flex-shrink-0" />
                    <div>
                        <div className="text-sm font-black tracking-tighter text-amber-900">
                            {pending.length} {pending.length === 1 ? "mese arretrato" : "mesi arretrati"} da chiudere
                        </div>
                        <div className="text-xs text-amber-800 mt-1 leading-relaxed">
                            I mesi passati vanno chiusi in ordine cronologico. Il P&L di ogni
                            mese viene accantonato (positivo o negativo) nel wallet Utili.
                            {emptyCount > 0 && (
                                <>
                                    {" "}<strong>{emptyCount}</strong>{" "}
                                    {emptyCount === 1 ? "mese è vuoto" : "mesi sono vuoti"}{" "}
                                    (nessuna transazione).
                                </>
                            )}
                        </div>
                    </div>
                </div>
                <button
                    onClick={onCloseAll}
                    disabled={bulkRunning}
                    className="btn-primary inline-flex items-center gap-2 flex-shrink-0 disabled:opacity-30"
                    data-testid="bulk-close-pending"
                >
                    <LockKey size={14} weight="bold" />
                    {bulkRunning ? "Chiusura…" : `Chiudi tutti (${pending.length})`}
                </button>
            </div>
            <div className="bg-white border border-amber-200 overflow-x-auto">
                <table className="ft-table" data-testid="pending-table">
                    <thead>
                        <tr>
                            <th>Mese</th>
                            <th className="text-right">Entrate</th>
                            <th className="text-right">Uscite</th>
                            <th className="text-right">P&L stimato</th>
                            <th className="text-right">Transazioni</th>
                            <th className="text-right">Azione</th>
                        </tr>
                    </thead>
                    <tbody>
                        {pending.map((p) => {
                            const isEmpty = p.n_transactions === 0;
                            return (
                                <tr
                                    key={p.year_month}
                                    data-testid={`pending-row-${p.year_month}`}
                                    className={isEmpty ? "text-zinc-400" : ""}
                                >
                                    <td className="font-bold">
                                        <div className="flex items-center gap-2">
                                            {p.year_month}
                                            {isEmpty && (
                                                <span className="px-2 py-0.5 bg-zinc-100 text-zinc-500 text-[9px] font-bold uppercase tracking-wider">
                                                    vuoto
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="text-right tabular-nums">{euro(p.entrate)}</td>
                                    <td className="text-right tabular-nums">{euro(p.uscite)}</td>
                                    <td
                                        className="text-right tabular-nums font-bold"
                                        style={{
                                            color: isEmpty
                                                ? "#a1a1aa"
                                                : p.pnl >= 0
                                                  ? "#00C853"
                                                  : "#FF2A00",
                                        }}
                                    >
                                        {euro(p.pnl)}
                                    </td>
                                    <td className="text-right tabular-nums text-xs">
                                        {p.n_transactions}
                                    </td>
                                    <td className="text-right">
                                        <button
                                            onClick={() => onCloseOne(p.year_month)}
                                            disabled={saving || bulkRunning}
                                            className="btn-secondary !py-2 !px-3 inline-flex items-center gap-2 disabled:opacity-30"
                                            data-testid={`pending-close-${p.year_month}`}
                                        >
                                            <LockKey size={12} weight="bold" />
                                            Chiudi
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
